import { useLocation } from "react-router-dom";
import { useMemo } from "react";
import { useAgentData } from "@/modules/agent/states";
import { AGENT_TYPES, RiskAssessmentTypes } from "@/modules/agent/types";

const AGENT_TYPE_TO_SIDEBAR_SECTION: Partial<Record<AGENT_TYPES, string>> = {
	[AGENT_TYPES.RISK_ASSESSMENT]: "compliance",
	[AGENT_TYPES.GAP_ASSESSMENT]: "compliance",
	[AGENT_TYPES.MULTI_FILE_VENDOR_ASSESSMENT]: "third-party-risk",
};

const AGENT_TYPE_TO_CHILD_PATH: Partial<Record<AGENT_TYPES, string>> = {
	[AGENT_TYPES.RISK_ASSESSMENT]: "compliance/gap-assessments",
	[AGENT_TYPES.GAP_ASSESSMENT]: "compliance/gap-assessments",
};

const DEEP_ROUTE_TO_CHILD: [RegExp, string][] = [];

function extractAgentId(pathname: string): string | null {
	const successMatch = pathname.match(/^\/agent\/success\/([^/]+)/);
	if (successMatch) return successMatch[1];
	const agentMatch = pathname.match(/^\/agent\/([^/]+)/);
	if (agentMatch && agentMatch[1] !== "success") return agentMatch[1];
	return null;
}

export function useActiveSidebarSection(): {
	section: string | null;
	childPath: string | null;
} {
	const { pathname } = useLocation();
	const agentId = extractAgentId(pathname);
	const agentData = useAgentData(agentId ?? "");

	return useMemo(() => {
		if (agentId && agentData) {
			const isFair =
				agentData.agentType === AGENT_TYPES.RISK_ASSESSMENT &&
				agentData.subType === RiskAssessmentTypes.FAIR;

			if (isFair) {
				return {
					section: "internal-risk",
					childPath: "internal-risk/risk-assessments",
				};
			}

			return {
				section:
					AGENT_TYPE_TO_SIDEBAR_SECTION[agentData.agentType] ?? null,
				childPath:
					AGENT_TYPE_TO_CHILD_PATH[agentData.agentType] ?? null,
			};
		}

		const deepMatch = DEEP_ROUTE_TO_CHILD.find(([pattern]) =>
			pattern.test(pathname),
		);
		if (deepMatch) {
			return { section: null, childPath: deepMatch[1] };
		}

		return { section: null, childPath: null };
	}, [agentId, agentData, pathname]);
}
